import { ProfilePagesUpdateMessage, useUpdateBroadcastChannel } from '@mvecom/common/hooks/useUpdateBroadcastChannel';
import { isAxiosError, AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Actions } from '../../actions/enums';
import { profileAxiosInstance as network } from '../../network/axios';
import { Endpoints, SiteTypes } from '../../network/enums';
import { endpointReplace } from '../../network/utils/endpointReplace';
import { getRetailUnitAndLanguage } from '../../network/utils/getRetailUnitAndLanguage';
import { LoginAuthEvent, useIcapsLogin } from './useIcapsLogin';
import { LogoutAuthEvent, useIcapsLogout } from './useIcapsLogout';

export type AuthEvent =
  | {
      status: 'initial-auth-check-pending' | 'initial-auth-check-true' | 'log-out-attempt-success';
    }
  | { status: 'initial-auth-check-false'; error?: AxiosError }
  | LoginAuthEvent
  | LogoutAuthEvent;

export const useIcaps = (enabled: boolean, onAuthEvent: (event: AuthEvent) => void) => {
  const [loggedIn, setLoggedIn] = useState<boolean>();
  const { retailUnit } = getRetailUnitAndLanguage();

  const onAuthRef = useRef(onAuthEvent);
  onAuthRef.current = onAuthEvent;

  const onLogoutAuthEvent = useCallback(
    (event: LogoutAuthEvent) => {
      if (event.status === 'log-out-attempt-success') {
        setLoggedIn(false);
      }
      onAuthEvent(event);
    },
    [onAuthEvent],
  );

  const onLoginAuthEvent = useCallback(
    (event: LoginAuthEvent) => {
      if (event.status === 'log-in-attempt-success') {
        setLoggedIn(true);
      }
      onAuthEvent(event);
    },
    [onAuthEvent],
  );

  const logout = useIcapsLogout(onLogoutAuthEvent);
  const login = useIcapsLogin(onLoginAuthEvent);

  // listen to other tabs updating
  useUpdateBroadcastChannel(
    'profile_pages_update',
    retailUnit,
    useCallback(
      (message: ProfilePagesUpdateMessage) => {
        if (!enabled) {
          return;
        }
        if (message.type === 'profile') {
          if (message.action === 'signed-in') {
            setLoggedIn(true);
            onAuthEvent({ status: 'log-in-attempt-success' });
          } else if (message.action === 'signed-out') {
            setLoggedIn(false);
            onAuthEvent({ status: 'log-out-attempt-success' });
          }
        }
      },
      [onAuthEvent, enabled],
    ),
  );

  // check login on start up
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (loggedIn !== undefined) {
      return;
    }

    let aborted = false;
    const controller = new AbortController();

    onAuthRef.current({ status: 'initial-auth-check-pending' });

    network
      .get(endpointReplace({ ':siteType': SiteTypes.PROFILE }, Endpoints.Auth), {
        signal: controller.signal,
      })
      .then((isLoggedInData) => {
        if (!aborted) {
          const isLoggedIn = !!isLoggedInData.data;

          if (isLoggedIn) {
            globalThis?.ikea?.pubsub?.publish?.(Actions.USER_INFO_AVAILABLE);
            onAuthRef.current({ status: 'initial-auth-check-true' });
            setLoggedIn(true);
          } else {
            onAuthRef.current({ status: 'initial-auth-check-false' });
            setLoggedIn(false);
          }
        }
      })
      .catch((e) => {
        if (!isAxiosError(e) || e.status !== 401) {
          console.error('error when auto logging in', e);
        }
        onAuthRef.current({
          status: 'initial-auth-check-false',
          error: isAxiosError(e) ? e : undefined,
        });
        setLoggedIn(false);
      });

    return () => {
      aborted = true;
      controller.abort();
    };
  }, [enabled, onAuthRef, loggedIn]);

  return useMemo(
    () => ({
      login,
      loggedIn: !!loggedIn,
      logout,
    }),
    [loggedIn, login, logout],
  );
};
